import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ParticleBackground } from "@/components/ui/magicui/particle-background";
import { useStripe } from "@/hooks/useStripe";
import { ORG_BILLING_ENABLED } from "@/lib/featureFlags";
import { cn } from "@/lib/utils";
import useAuthStore from "@/store/authStore";
import {
  ArrowLeft,
  Brain,
  FileUp,
  Image,
  Layers,
  Loader2,
  Search,
  Settings,
  Share2,
  Sparkles,
  Users,
  Video,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

/**
 * Parse JSON response safely
 * @param {Response} response - Fetch response
 * @returns {Promise<Object>} - Parsed JSON data or null
 */
const safelyParseJSON = async (response) => {
  try {
    const text = await response.text();
    return text ? JSON.parse(text) : null;
  } catch (error) {
    console.error("Error parsing JSON response:", error);
    return null;
  }
};

// Custom CSS animations
const customAnimations = `
  @keyframes float-up {
    0% { transform: translateY(0); opacity: 0.3; }
    100% { transform: translateY(-100vh); opacity: 0; }
  }

  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }

  @keyframes bounce-slow {
    0%, 100% { transform: translateY(-5%); }
    50% { transform: translateY(5%); }
  }

  @keyframes pulse-fast {
    0%, 100% { width: 20%; }
    50% { width: 100%; }
  }

  @keyframes pulse-slow {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 0.6; }
  }

  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }

  .animate-shimmer {
    position: absolute;
    inset: 2px;
    background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.1), transparent);
    background-size: 200% 100%;
    animation: shimmer 2s infinite;
    z-index: 0;
    pointer-events: none;
    border-radius: 0.25rem;
  }

  .animate-bounce-slow {
    animation: bounce-slow 3s ease-in-out infinite;
  }

  .animate-pulse-fast {
    animation: pulse-fast 1.5s ease-in-out infinite;
  }

  .animation-delay-2000 {
    animation-delay: 2s;
  }

  .animate-gradient-slow {
    background-size: 200% 200%;
    animation: gradient-slow 8s ease infinite;
  }

  .animate-pulse-slow {
    animation: pulse-slow 6s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }

  .animate-float {
    animation: float 6s ease-in-out infinite;
  }

  /* Light mode optimizations - scoped to light mode only */
  .light .checkout-page {
    background-color: #f9f7ff;
    color: #000000;
  }

  /* Dark mode optimizations - scoped to dark mode only */
  .dark .checkout-page {
    background-color: transparent;
    color: #ffffff;
  }

  /* Card styling - scoped to checkout cards */
  .light .checkout-card {
    background-color: rgba(255, 255, 255, 0.98);
    border-color: rgba(157, 109, 235, 0.35);
    box-shadow: 0 4px 20px rgba(157, 109, 235, 0.12);
    border-radius: 1rem;
  }

  .dark .checkout-card {
    background-color: rgba(0, 0, 0, 0.2);
    border-color: rgba(157, 109, 235, 0.2);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
    border-radius: 1rem;
  }

  .light .checkout-card:hover {
    box-shadow: 0 8px 30px rgba(157, 109, 235, 0.2);
  }

  .dark .checkout-card:hover {
    box-shadow: 0 8px 30px rgba(157, 109, 235, 0.15);
  }

  .light .checkout-glow {
    background: rgba(157, 109, 235, 0.15);
  }

  .dark .checkout-glow {
    background: rgba(157, 109, 235, 0.1);
  }

  .light .checkout-card-selected {
    border-color: rgba(157, 109, 235, 0.7);
    background-color: rgba(252, 250, 255, 1);
    box-shadow: 0 8px 30px rgba(157, 109, 235, 0.25);
  }

  .dark .checkout-card-selected {
    border-color: rgba(157, 109, 235, 0.5);
    background-color: rgba(20, 20, 30, 0.7);
    box-shadow: 0 8px 30px rgba(157, 109, 235, 0.15);
  }

  .light .checkout-divider {
    background: linear-gradient(to right, transparent, rgba(157, 109, 235, 0.3), transparent);
  }

  .dark .checkout-divider {
    background: linear-gradient(to right, transparent, rgba(157, 109, 235, 0.2), transparent);
  }

  /* Button optimizations - scoped to checkout page */
  .light .checkout-page button.checkout-button {
    color: white !important;
  }

  .dark .checkout-page button.checkout-button {
    color: white !important;
  }

  .light .checkout-page button.gradient-button {
    background-image: linear-gradient(to right, #7c3aed, #3b82f6);
    box-shadow: 0 2px 10px rgba(124, 58, 237, 0.3);
    color: white !important;
  }

  .dark .checkout-page button.gradient-button {
    background-image: linear-gradient(to right, #7c3aed, #3b82f6);
    box-shadow: 0 2px 10px rgba(124, 58, 237, 0.2);
    color: white !important;
  }

  .light .checkout-page button.transparent-button:hover {
    background-color: rgba(124, 58, 237, 0.1);
    color: white !important;
  }

  .dark .checkout-page button.transparent-button:hover {
    background-color: rgba(124, 58, 237, 0.2);
    color: white !important;
  }

  /* Make sure hover states work correctly */
  .light .checkout-page button.hover-white:hover {
    color: white !important;
  }

  .dark .checkout-page button.hover-white:hover {
    color: white !important;
  }

  /* Background elements adjustment */
  .light .checkout-blur {
    opacity: 0.5;
    border-radius: 0;
  }

  .dark .checkout-blur {
    opacity: 0.2;
    border-radius: 0;
  }

  /* Feature check elements */
  .light .checkout-page .check-icon-bg {
    background: transparent;
  }

  .dark .checkout-page .check-icon-bg {
    background: transparent;
  }

  .light .checkout-page .check-icon {
    color: #ffffff !important;
  }

  .dark .checkout-page .check-icon {
    color: #ffffff !important;
  }

  /* Text colors - scoped to checkout page only */
  .light .checkout-page .feature-item {
    color: #333333 !important;
  }

  .dark .checkout-page .feature-item {
    color: rgba(255, 255, 255, 0.9) !important;
  }

  .light .checkout-page .feature-subtitle {
    color: #333333 !important;
  }

  .dark .checkout-page .feature-subtitle {
    color: rgba(187, 153, 255, 0.8) !important;
  }

  .light .checkout-page .feature-price {
    color: #333333 !important;
  }

  .dark .checkout-page .feature-price {
    color: #ffffff !important;
  }

  .light .checkout-page .strikethrough-price {
    color: #555555;
    opacity: 0.7;
  }

  .dark .checkout-page .strikethrough-price {
    color: #b388ff;
    opacity: 0.7;
  }

  /* Card headers and plan names */
  .light .checkout-page .plan-name {
    color: #333333 !important;
  }

  .dark .checkout-page .plan-name {
    color: #ffffff !important;
  }

  /* Main pricing header */
  .light .checkout-page .pricing-title {
    background-image: linear-gradient(to right, #5b21b6, #2563eb, #9d174d) !important;
  }

  .dark .checkout-page .pricing-title {
    background-image: linear-gradient(to right, #c4b5fd, #93c5fd, #f9a8d4) !important;
  }

  /* Pricing subtitle */
  .light .checkout-page .pricing-subtitle {
    color: #333333 !important;
  }

  .dark .checkout-page .pricing-subtitle {
    color: rgba(216, 180, 254, 0.9) !important;
  }

  /* Make cards more compact */
  .checkout-card {
    padding: 0.75rem !important;
  }

  .checkout-card ul {
    margin-bottom: 0.75rem !important;
  }

  .checkout-card ul li {
    margin-top: 0 !important;
    margin-bottom: 0.25rem !important;
  }

  .checkout-card .mt-auto {
    margin-top: 0.5rem !important;
  }

  .checkout-card .mb-2 {
    margin-bottom: 0.25rem !important;
  }

  .checkout-card .mb-4 {
    margin-bottom: 0.5rem !important;
  }

  .checkout-card .my-2 {
    margin-top: 0.25rem !important;
    margin-bottom: 0.25rem !important;
  }

  /* Improve spacing in feature items */
  .checkout-card .flex-shrink-0.mt-0.5 {
    margin-top: 0.125rem !important;
  }

  /* Additional text color fixes for light mode - EXCLUDE pricing cards */
  .light .checkout-page h1:not(.pricing-card *),
  .light .checkout-page h2:not(.pricing-card *),
  .light .checkout-page h3:not(.pricing-card *) {
    color: #111111 !important;
  }

  .light .checkout-page p:not(.pricing-card *) {
    color: #333333 !important;
  }

  .light .checkout-page .text-muted-foreground:not(.pricing-card *) {
    color: #555555 !important;
  }

  .light .checkout-page .pricing-title.bg-clip-text {
    -webkit-text-fill-color: transparent;
  }

  /* Fix for plan descriptions in light mode - EXCLUDE pricing cards */
  .light .checkout-page .text-xs.feature-subtitle:not(.pricing-card *) {
    color: #333333 !important;
  }

  /* Fix for any remaining text elements - EXCLUDE pricing cards */
  .light .checkout-page span:not([class*="text-"]):not(.pricing-card *) {
    color: #333333 !important;
  }

  /* Special handling for code inside tables */
  .light .checkout-page td,
  .light .checkout-page th {
    color: #333333 !important;
  }

  /* Ensure all headings have proper contrast in light mode - EXCLUDE pricing cards */
  .light .checkout-page [class*="text-lg"]:not(.pricing-card *),
  .light .checkout-page [class*="text-xl"]:not(.pricing-card *),
  .light .checkout-page [class*="text-2xl"]:not(.pricing-card *),
  .light .checkout-page [class*="text-3xl"]:not(.pricing-card *) {
    color: #333333 !important;
  }

  /* Pricing cards should keep their own colors regardless of theme */
  .pricing-card,
  .pricing-card * {
    /* Let the component classes control text colors */
  }

  .pricing-card .text-white {
    color: white !important;
  }

  .pricing-card .text-gray-400 {
    color: #9ca3af !important;
  }

  .pricing-card .text-gray-300 {
    color: #d1d5db !important;
  }

  .pricing-card .text-gray-200 {
    color: #e5e7eb !important;
  }

  .pricing-card .text-purple-100 {
    color: #ede9fe !important;
  }

  .pricing-card .text-purple-200 {
    color: #ddd6fe !important;
  }

  .pricing-card .text-purple-50 {
    color: #faf5ff !important;
  }

  /* FAQ section - light mode styling */
  .light .checkout-page .faq-section h2 {
    color: #1f2937 !important;
  }

  .light .checkout-page .faq-section > div > p.text-gray-400 {
    color: #6b7280 !important;
  }

  .light .checkout-page .faq-section .text-white {
    color: #1f2937 !important;
  }

  .light .checkout-page .faq-section [data-state] {
    color: #1f2937 !important;
  }

  .light .checkout-page .faq-section .bg-gray-800\\/80 {
    background-color: rgba(243, 244, 246, 0.95) !important;
    border: 1px solid #e5e7eb;
  }

  .light .checkout-page .faq-section .text-gray-400 {
    color: #6b7280 !important;
  }

  /* FAQ section - dark mode styling (ensure proper colors) */
  .dark .checkout-page .faq-section h2 {
    color: white !important;
  }

  .dark .checkout-page .faq-section .text-gray-400 {
    color: #9ca3af !important;
  }

  .dark .checkout-page .faq-section .text-white {
    color: white !important;
  }

  .dark .checkout-page .faq-section [data-state] {
    color: white !important;
  }

  .dark .checkout-page .faq-section .bg-gray-800\\/80 {
    background-color: rgba(31, 41, 55, 0.8) !important;
  }

  /* Billing cycle toggle - light mode styling */
  .light .checkout-page .billing-toggle {
    background-color: #f3f4f6 !important;
    border-color: #e5e7eb !important;
  }

  .light .checkout-page .billing-toggle .text-gray-400 {
    color: #6b7280 !important;
  }

  .light .checkout-page .billing-toggle .text-white {
    color: #111827 !important;
  }

  .light .checkout-page .billing-toggle .bg-gray-700 {
    background-color: white !important;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  .light .checkout-page .billing-toggle .text-green-400 {
    color: #22c55e !important;
  }

  /* Billing cycle toggle - dark mode styling (ensure proper colors) */
  .dark .checkout-page .billing-toggle {
    background-color: rgb(31, 41, 55) !important;
    border-color: rgb(55, 65, 81) !important;
  }

  .dark .checkout-page .billing-toggle .text-gray-400 {
    color: #9ca3af !important;
  }

  .dark .checkout-page .billing-toggle .text-white {
    color: white !important;
  }

  .dark .checkout-page .billing-toggle .bg-gray-700 {
    background-color: rgb(55, 65, 81) !important;
  }

  .dark .checkout-page .billing-toggle .text-green-400 {
    color: #4ade80 !important;
  }

  /* Main header - dark text in light mode, white text in dark mode */
  .light .checkout-page .pricing-header .text-white,
  .light .checkout-page .pricing-header h1 {
    color: #1f2937 !important;
  }

  .light .checkout-page .pricing-header .text-gray-300,
  .light .checkout-page .pricing-header p {
    color: #4b5563 !important;
  }

  /* Ensure dark mode keeps white text for header */
  .dark .checkout-page .pricing-header .text-white,
  .dark .checkout-page .pricing-header h1 {
    color: white !important;
  }

  .dark .checkout-page .pricing-header .text-gray-300,
  .dark .checkout-page .pricing-header p {
    color: #d1d5db !important;
  }
`;

// Feature icons mapping
const FeatureIcons = {
  models: Sparkles,
  files: FileUp,
  research: Search,
  image: Image,
  memory: Brain,
  projects: Layers,
  video: Video,
  team: Users,
  settings: Settings,
  share: Share2,
  priority: Zap,
};

// Pricing plan data with monthly and yearly prices (matching screenshot design)
const PLANS = {
  PRO: {
    id: "pro",
    name: "Pro",
    price: {
      monthly: 19.99,
      yearly: 17.99,
    },
    yearlyTotal: 216,
    interval: "month",
    description: "For professionals and creators",
    tagline: "Get started today:",
    features: [
      { text: "Unlimited Premium Model Access", icon: "models" },
      { text: "Unlimited File Uploads", icon: "files" },
      { text: "Unlimited Deep Research", icon: "research" },
      { text: "Access to Premium Image Gen models", icon: "image" },
      { text: "Expanded memory and context", icon: "memory" },
      { text: "Unlimited Projects and Tasks", icon: "projects" },
      { text: "Limited video creation", icon: "video" },
    ],
    highlight: true,
    popular: true,
  },
  TEAM: {
    id: "team",
    name: "Team",
    price: {
      monthly: 29.99,
      yearly: 26.99,
    },
    interval: "month",
    description: "Perfect for teams and organizations",
    tagline: "Everything in Pro +",
    features: [
      { text: "Everything in Pro, plus:", icon: "models" },
      { text: "Enhanced research capabilities", icon: "research" },
      { text: "Unlimited shared projects", icon: "share" },
      { text: "Team management and controls", icon: "settings" },
      { text: "Priority access to best AI models", icon: "priority" },
    ],
    highlight: false,
    popular: false,
  },
};

// PricingCard component matching screenshot design
const PricingCard = ({
  plan,
  billingCycle,
  onClick,
  isLoading,
  loadingPlan,
}) => {
  // Get price based on billing cycle
  const price =
    billingCycle === "yearly" ? plan.price.yearly : plan.price.monthly;

  const isThisPlanLoading = isLoading && loadingPlan === plan.id;
  const isTeamPlan = plan.id === "team";
  const isTeamDisabled = isTeamPlan && !ORG_BILLING_ENABLED;
  const isProPlan = plan.id === "pro";

  return (
    <div
      className={`pricing-card relative rounded-2xl p-6 h-full overflow-hidden transition-all duration-300 ${
        isProPlan
          ? "bg-gradient-to-br from-purple-500 via-purple-600 to-violet-600"
          : "bg-gray-900 dark:bg-gray-800 border border-gray-700"
      }`}
    >
      {/* Plan name with inline Popular badge */}
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-xl font-semibold text-white">{plan.name}</h3>
        {plan.popular && (
          <span className="inline-block bg-black/30 text-white text-xs font-medium px-3 py-1 rounded-full">
            Popular
          </span>
        )}
      </div>

      {/* Price */}
      <div className="mb-2">
        <div className="flex items-baseline">
          <span className="text-4xl font-bold text-white">${price}</span>
          <span
            className={`ml-2 text-base ${
              isProPlan ? "text-purple-100" : "text-gray-400"
            }`}
          >
            /month
          </span>
        </div>
      </div>

      {/* Description */}
      <p
        className={`text-sm mb-4 ${
          isProPlan ? "text-purple-100" : "text-gray-300"
        }`}
      >
        {plan.description}
      </p>

      {/* Action button - positioned before features */}
      <button
        onClick={() => {
          if (!isTeamDisabled)
            onClick(plan.id, billingCycle === "yearly" ? "year" : "month");
        }}
        disabled={isLoading || isTeamDisabled}
        className={`w-full rounded-lg px-4 py-3 text-sm font-medium transition-all duration-300 mb-6 ${
          isProPlan
            ? "bg-white text-purple-600 hover:bg-purple-50"
            : isTeamDisabled
              ? "bg-gray-700 text-gray-500 cursor-not-allowed"
              : "bg-purple-600 text-white hover:bg-purple-700"
        }`}
      >
        {isThisPlanLoading ? (
          <span className="flex items-center justify-center">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </span>
        ) : isTeamDisabled ? (
          "Coming Soon"
        ) : (
          "Try for Free"
        )}
      </button>

      {/* Tagline */}
      <p
        className={`text-sm font-medium mb-4 ${
          isProPlan ? "text-white" : "text-gray-200"
        }`}
      >
        {plan.tagline}
      </p>

      {/* Features list with icons */}
      <ul className="space-y-3 flex-1">
        {plan.features.map((feature, index) => {
          const IconComponent = FeatureIcons[feature.icon] || Sparkles;
          return (
            <li key={index} className="flex items-start">
              <IconComponent
                className={`h-4 w-4 mr-3 flex-shrink-0 mt-0.5 ${
                  isProPlan ? "text-purple-200" : "text-gray-400"
                }`}
              />
              <span
                className={`text-sm ${
                  isProPlan ? "text-purple-50" : "text-gray-300"
                }`}
              >
                {feature.text}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const CheckoutForm = () => {
  const [loading, setLoading] = useState(false); // Don't show loading initially
  const [error, setError] = useState(null);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [plans, setPlans] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly"); // Default to monthly billing
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [buttonLoading, setButtonLoading] = useState(false);
  const { user } = useAuthStore();
  const {
    createSubscriptionCheckout,
    createSubscriptionCheckoutWith1MonthFree,
    getSubscriptionPlans,
  } = useStripe();
  const navigate = useNavigate();
  const isFreePlan =
    user?.subscription?.plan === "free" || !user?.subscription?.plan;

  // Set selected plan based on user's current plan
  useEffect(() => {
    // Don't automatically select a plan - let users browse pricing first
    // setSelectedPlan("pro"); // Default to pro plan
  }, [user]);

  // Fetch available subscription plans
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const plansData = await getSubscriptionPlans();
        setPlans(plansData.plans);
      } catch (err) {
        console.error("Error fetching plans:", err);
        // Don't set error state here, as we'll still try to create a checkout session
      }
    };

    fetchPlans();
  }, []);

  useEffect(() => {
    const initializeCheckout = async () => {
      try {
        // Only initialize checkout if a plan is actually selected
        if (!selectedPlan) {
          setLoading(false);
          return;
        }

        // If not logged in, redirect to signup
        if (!user) {
          navigate(`/auth/signup?plan=${selectedPlan}&billing=${billingCycle}`);
          return;
        }

        const billingInterval = billingCycle === "yearly" ? "year" : "month";

        // Initialize subscription checkout using our backend API with 1 month free for new users
        const response = await createSubscriptionCheckoutWith1MonthFree({
          planId: selectedPlan, // Use the selected plan ID
          billingInterval, // Pass the billing interval
          successUrl: `${window.location.origin}/chat?checkout_success=true&plan=${selectedPlan}`,
          cancelUrl: `${window.location.origin}/`,
          customerEmail: user.email,
          allowPromotionCodes: true,
        });

        if (!response.url) {
          console.error("No checkout URL received");
          throw new Error("Failed to create checkout session");
        }

        setCheckoutUrl(response.url);

        // Redirect to checkout immediately when URL is available
        window.location.href = response.url;
      } catch (err) {
        console.error("Error creating checkout session:", err);
        setError(err.message || "Failed to set up checkout. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    // Only run initialization when a plan is selected
    if (selectedPlan) {
      initializeCheckout();
    } else {
      setLoading(false);
    }
  }, [user, selectedPlan, billingCycle, navigate]);

  const handleSelectPlan = (planId) => {
    // If not logged in, redirect to sign up
    if (!user) {
      navigate(`/auth/signup?plan=${planId}&billing=${billingCycle}`);
      return;
    }

    setSelectedPlan(planId);
    setLoading(true); // Show loading while checkout is initialized
  };

  const handleSubscription = async (planId, billingInterval) => {
    // If user is not logged in, redirect to signup with plan info
    if (!user) {
      navigate(
        `/auth/signup?plan=${planId}&billing=${billingInterval === "year" ? "yearly" : "monthly"}`,
        { replace: true }
      );
      return;
    }

    // Set the selected plan to trigger the initialization flow
    setSelectedPlan(planId);
    setBillingCycle(billingInterval === "year" ? "yearly" : "monthly");
    setLoading(true);
  };

  if (loading) {
    return (
      <div
        className={cn(
          "checkout-page relative min-h-screen overflow-hidden py-12"
        )}
      >
        {/* Back button for authenticated users */}
        {user && (
          <div className="relative z-20 w-full px-6 py-4">
            <button
              onClick={() => navigate("/chat")}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-transparent border border-purple-500/40 hover:bg-white/10 transition-all duration-200 text-white shadow-md hover:shadow-lg text-sm font-medium"
              aria-label="Back to chat"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Chat
            </button>
          </div>
        )}

        {/* Animated background elements */}
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
          <div
            className={cn(
              "absolute -left-64 -top-64 h-[40rem] w-[40rem] rounded-[3rem] blur-3xl checkout-blur"
            )}
          ></div>
          <div
            className={cn(
              "absolute -bottom-32 -right-32 h-[30rem] w-[30rem] rounded-[3rem] blur-3xl checkout-blur"
            )}
          ></div>
          <div
            className={cn(
              "animation-delay-2000 absolute right-1/4 top-1/2 h-[20rem] w-[20rem] animate-pulse rounded-[3rem] blur-3xl checkout-blur"
            )}
          ></div>
        </div>

        <div className="relative z-10 flex min-h-screen items-center justify-center px-4">
          <div
            className={cn(
              "rounded-2xl backdrop-blur-md p-8 text-center",
              "border border-purple-500/20",
              "shadow-lg shadow-purple-500/5"
            )}
          >
            <div className="mb-6 flex justify-center">
              <div className="relative">
                <div
                  className={cn(
                    "absolute inset-0 animate-spin rounded-full border-b-2",
                    "border-purple-400"
                  )}
                ></div>
                <div className="relative flex h-20 w-20 items-center justify-center rounded-full">
                  <svg
                    className="h-10 w-10 text-purple-500"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M20 4L3 9.8V11m17-7L10 21m10-17L15.9 11H3"
                    />
                  </svg>
                </div>
              </div>
            </div>
            <h3
              className={cn(
                "mb-2 text-2xl font-bold",
                "text-white dark:text-white light:text-gray-900"
              )}
            >
              Setting Up Your Payment
            </h3>
            <p
              className={cn(
                "mb-4 text-lg",
                "text-purple-200 dark:text-purple-200 light:text-gray-700"
              )}
            >
              We&apos;re securely connecting to our payment provider
            </p>
            <div
              className={cn(
                "mx-auto w-full max-w-xs overflow-hidden rounded-full p-1",
                "bg-purple-900/30 dark:bg-purple-900/30 light:bg-purple-200/70"
              )}
            >
              <div
                className={cn(
                  "animate-pulse-fast h-1.5 rounded-full",
                  "bg-gradient-to-r from-purple-500 to-blue-500"
                )}
              ></div>
            </div>
            <p
              className={cn(
                "mt-6 text-sm",
                "text-purple-300 dark:text-purple-300 light:text-gray-600"
              )}
            >
              This will only take a moment
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={cn(
          "checkout-page flex min-h-screen items-center justify-center"
        )}
      >
        {/* Back button for authenticated users */}
        {user && (
          <div className="absolute top-0 left-0 z-20 w-full px-6 py-4">
            <button
              onClick={() => navigate("/chat")}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-transparent border border-purple-500/40 hover:bg-white/10 transition-all duration-200 text-white shadow-md hover:shadow-lg text-sm font-medium"
              aria-label="Back to chat"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Chat
            </button>
          </div>
        )}

        <div
          className={cn(
            "max-w-md rounded-xl backdrop-blur-md p-8 text-center shadow-lg",
            "border border-red-500/20"
          )}
        >
          <p
            className={cn(
              "mb-4 text-lg",
              "text-red-300 dark:text-red-300 light:text-red-600"
            )}
          >
            {error}
          </p>
          <button
            onClick={() => navigate("/chat")}
            className={cn(
              "rounded-md px-6 py-2.5 text-sm font-medium text-white transition-colors focus:ring-2 focus:ring-purple-400 focus:ring-offset-2",
              "bg-purple-600 hover:bg-purple-500"
            )}
          >
            Return to Chat
          </button>
        </div>
      </div>
    );
  }

  // Get prices from API or use defaults
  const pricingData = {
    PRO: PLANS.PRO, // Use static pricing data
    TEAM: PLANS.TEAM, // Use static pricing data
  };

  // Render the button text based on authentication status and current plan
  const getButtonText = (planId) => {
    if (!user) {
      return "Try Free";
    }

    // Check if this is the user's current plan
    const currentPlan = user?.subscription?.plan || "free";
    if (currentPlan === planId) {
      return "Current";
    }

    return "Try Free";
  };

  // FAQ component matching screenshot design
  const PricingFAQs = () => {
    return (
      <section className="faq-section py-16 max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Frequently Asked
            <br />
            Questions
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Everything you need to know about Artifact and how it can transform
            the way you work
          </p>
        </div>

        <Accordion type="single" collapsible className="w-full space-y-3">
          <AccordionItem
            value="item-1"
            className="border-0 bg-gray-100 dark:bg-gray-800/80 rounded-lg overflow-hidden"
          >
            <AccordionTrigger className="text-left font-medium py-4 px-5 text-gray-900 dark:text-white hover:no-underline data-[state=open]:no-underline [&[data-state=open]>svg]:rotate-180">
              What is Artifact and who is it for?
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-4 text-gray-600 dark:text-gray-400 text-sm">
              Artifact is an AI assistant platform for anyone who wants to work
              smarter, not harder. Whether you&apos;re a student, professional,
              creative, or business owner, Artifact helps with writing,
              research, planning, and creative projects all in one place.
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="item-2"
            className="border-0 bg-gray-100 dark:bg-gray-800/80 rounded-lg overflow-hidden"
          >
            <AccordionTrigger className="text-left font-medium py-4 px-5 text-gray-900 dark:text-white hover:no-underline data-[state=open]:no-underline [&[data-state=open]>svg]:rotate-180">
              How do I get started with Artifact?
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-4 text-gray-600 dark:text-gray-400 text-sm">
              Getting started is simple! Sign up for a free account, and
              you&apos;ll have immediate access to multiple AI models. No
              technical setup required - just start chatting with AI assistants
              that can help with any task you have in mind.
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="item-3"
            className="border-0 bg-gray-100 dark:bg-gray-800/80 rounded-lg overflow-hidden"
          >
            <AccordionTrigger className="text-left font-medium py-4 px-5 text-gray-900 dark:text-white hover:no-underline data-[state=open]:no-underline [&[data-state=open]>svg]:rotate-180">
              Can I use Artifact with my existing apps?
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-4 text-gray-600 dark:text-gray-400 text-sm">
              Yes! Artifact integrates with popular tools you already use,
              including email platforms, document editors, project management
              tools, and more. Our integrations make it easy to incorporate AI
              assistance into your existing workflow.
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="item-4"
            className="border-0 bg-gray-100 dark:bg-gray-800/80 rounded-lg overflow-hidden"
          >
            <AccordionTrigger className="text-left font-medium py-4 px-5 text-gray-900 dark:text-white hover:no-underline data-[state=open]:no-underline [&[data-state=open]>svg]:rotate-180">
              What&apos;s included in the Pro plan?
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-4 text-gray-600 dark:text-gray-400 text-sm">
              The Pro plan gives you access to the best AI models, unlimited
              personal projects, enhanced research capabilities, early access to
              new features, and the ability to create custom AI assistants
              tailored to your specific needs. Plus priority support when you
              need help.
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="item-5"
            className="border-0 bg-gray-100 dark:bg-gray-800/80 rounded-lg overflow-hidden"
          >
            <AccordionTrigger className="text-left font-medium py-4 px-5 text-gray-900 dark:text-white hover:no-underline data-[state=open]:no-underline [&[data-state=open]>svg]:rotate-180">
              Can I collaborate with others on Artifact?
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-4 text-gray-600 dark:text-gray-400 text-sm">
              Absolutely! You can share projects and workspaces with teammates,
              classmates, or collaborators. Work together on documents,
              brainstorm ideas, and share AI conversations seamlessly. It&apos;s
              perfect for team projects, group assignments, or collaborative
              creative work.
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="item-6"
            className="border-0 bg-gray-100 dark:bg-gray-800/80 rounded-lg overflow-hidden"
          >
            <AccordionTrigger className="text-left font-medium py-4 px-5 text-gray-900 dark:text-white hover:no-underline data-[state=open]:no-underline [&[data-state=open]>svg]:rotate-180">
              Is my data secure with Artifact?
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-4 text-gray-600 dark:text-gray-400 text-sm">
              Your privacy and security are our top priorities. We use
              enterprise-grade security measures including encryption and secure
              data handling. Your conversations and projects remain private to
              you and only those you choose to share them with.
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>
    );
  };

  return (
    <>
      <Helmet>
        <title>Pricing Plans | Artifact - AI Assistant Subscription</title>
        <meta
          name="description"
          content="Choose the perfect AI assistant plan for your needs. Pro plans start at $19.99/month with access to ChatGPT-5, Claude, Gemini, and 30+ AI models. Free trial available."
        />

        {/* Keywords */}
        <meta
          name="keywords"
          content="AI assistant pricing, GPT-5 subscription, Claude AI pricing, AI model access, productivity AI plans, team AI collaboration, enterprise AI pricing"
        />

        {/* Canonical URL */}
        <link rel="canonical" href="https://artifact.chat/pricing" />

        {/* Open Graph Meta Tags */}
        <meta
          property="og:title"
          content="Pricing Plans | Artifact - AI Assistant Subscription"
        />
        <meta
          property="og:description"
          content="Choose the perfect AI assistant plan for your needs. Pro plans start at $19.99/month with access to ChatGPT-5, Claude, Gemini, and 30+ AI models. Free trial available."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://artifact.chat/pricing" />
        <meta
          property="og:image"
          content="https://artifact.chat/assets/og-image.jpeg"
        />
        <meta property="og:site_name" content="Artifact" />

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Pricing Plans | Artifact - AI Assistant Subscription"
        />
        <meta
          name="twitter:description"
          content="Choose the perfect AI assistant plan for your needs. Pro plans start at $19.99/month with access to ChatGPT-5, Claude, Gemini, and 30+ AI models. Free trial available."
        />
        <meta
          name="twitter:image"
          content="https://artifact.chat/assets/og-image.jpeg"
        />
        <meta name="twitter:site" content="@ArtifactAI" />

        {/* Additional Meta Tags */}
        <meta name="robots" content="index, follow" />

        {/* Structured Data for Pricing */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: "Artifact AI Assistant",
            description:
              "Unite all leading AI models in one workspace. Chat with ChatGPT, Claude, Gemini & more.",
            url: "https://artifact.chat/pricing",
            offers: [
              {
                "@type": "Offer",
                name: "Pro Plan - Monthly",
                price: "19.99",
                priceCurrency: "USD",
                billingIncrement: "P1M",
                description:
                  "Access to premium AI models, unlimited projects, and advanced features",
              },
              {
                "@type": "Offer",
                name: "Pro Plan - Yearly",
                price: "17.99",
                priceCurrency: "USD",
                billingIncrement: "P1M",
                description:
                  "Annual billing with 10% savings - Access to premium AI models, unlimited projects, and advanced features",
              },
              {
                "@type": "Offer",
                name: "Team Plan - Monthly",
                price: "29.99",
                priceCurrency: "USD",
                billingIncrement: "P1M",
                description:
                  "Everything in Pro plus team collaboration, admin management, and extended AI model usage",
              },
            ],
            brand: {
              "@type": "Brand",
              name: "Artifact",
            },
          })}
        </script>
      </Helmet>

      <div className="checkout-page min-h-screen relative overflow-hidden">
        <style dangerouslySetInnerHTML={{ __html: customAnimations }} />

        {/* Interactive Particle Background */}
        <div className="absolute inset-0 overflow-hidden">
          <ParticleBackground
            quantity={30}
            speed={0.3}
            color="#a855f7"
            connectThreshold={150}
            minSize={1}
            maxSize={2}
          />
        </div>

        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 via-blue-600/10 to-purple-800/20 dark:from-purple-900/40 dark:via-blue-900/20 dark:to-purple-950/40 light:from-purple-100/60 light:via-blue-100/30 light:to-purple-200/60" />

        {/* Main Content */}
        <div className="relative z-10 mx-auto max-w-6xl px-4 py-8">
          {/* Header */}
          <div className="pricing-header mb-12">
            <div className="text-center mb-6">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white italic mb-4">
                Simple pricing for everyone
              </h1>
              <p className="text-gray-600 dark:text-gray-300 text-base sm:text-lg max-w-2xl mx-auto">
                Choose the plan that fits your needs with transparent pricing.
                <br />
                No hidden costs, no surprises — just powerful AI assistance at
                your fingertips.
              </p>
            </div>

            {/* Billing Cycle Toggle - matching screenshot design */}
            <div className="flex justify-center mb-10">
              <div className="billing-toggle inline-flex p-1 rounded-lg bg-gray-800 border border-gray-700">
                <button
                  type="button"
                  onClick={() => setBillingCycle("monthly")}
                  className={`px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    billingCycle === "monthly"
                      ? "bg-gray-700 text-white"
                      : "bg-transparent text-gray-400 hover:text-gray-300"
                  }`}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setBillingCycle("yearly")}
                  className={`px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                    billingCycle === "yearly"
                      ? "bg-gray-700 text-white"
                      : "bg-transparent text-gray-400 hover:text-gray-300"
                  }`}
                >
                  Yearly
                  <span className="text-green-400 text-xs font-normal">
                    Save 10%
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid gap-4 md:gap-6 md:grid-cols-2 max-w-3xl mx-auto px-2 sm:px-4">
            <PricingCard
              plan={pricingData.PRO}
              billingCycle={billingCycle}
              onClick={handleSubscription}
              isLoading={buttonLoading}
              loadingPlan={loadingPlan}
            />

            <PricingCard
              plan={pricingData.TEAM}
              billingCycle={billingCycle}
              onClick={handleSubscription}
              isLoading={buttonLoading}
              loadingPlan={loadingPlan}
            />
          </div>

          {/* Pricing disclaimer */}
          <div className="text-center mt-8 text-xs text-muted-foreground px-4">
            Prices shown do not include applicable taxes. Your subscription will
            renew automatically.
          </div>

          {/* Add the FAQs Section here */}
          <PricingFAQs />
        </div>

        {/* Error Dialog */}
        <Dialog open={!!error} onOpenChange={() => setError(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Error</DialogTitle>
              <DialogDescription>{error}</DialogDescription>
            </DialogHeader>
            <div className="flex justify-end">
              <Button onClick={() => setError(null)}>Close</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

const ReturnPage = () => {
  const [status, setStatus] = useState("loading");
  const [customerEmail, setCustomerEmail] = useState("");
  const [searchParams] = useSearchParams();
  const { updateUserSettings } = useAuthStore();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  useEffect(() => {
    const verifyPayment = async () => {
      const success = searchParams.get("success");
      const sessionId = searchParams.get("session_id");

      if (success === "true") {
        try {
          // If no user, redirect to signup
          if (!user) {
            navigate("/auth/signup?payment_success=true");
            return;
          }

          // If we have a session ID, verify it with the server
          if (sessionId) {
            try {
              const response = await fetch(
                `/api/stripe/session-status?session_id=${sessionId}`
              );

              if (!response.ok) {
                throw new Error("Failed to verify session");
              }

              const data = await safelyParseJSON(response);
              if (!data) {
                console.warn("Empty response from session status endpoint");
                // Continue anyway, as we want to be resilient
              } else {
                const { status, customer_email } = data;

                if (status !== "complete" && status !== "paid") {
                  throw new Error("Payment not completed");
                }

                if (customer_email) {
                  setCustomerEmail(customer_email);
                }
              }
            } catch (sessionErr) {
              console.error("Error verifying session:", sessionErr);
              // Continue with update even if session verification fails
              // This is fallback behavior - ideally webhook would handle this
            }
          }

          // Update the user's subscription status
          await updateUserSettings({
            subscription: {
              plan: "pro",
              status: "active",
              updated_at: new Date().toISOString(),
            },
          });

          setStatus("complete");

          // Redirect to chat page with success parameter
          navigate("/chat?checkout_success=true&plan=pro");
        } catch (error) {
          console.error("Failed to update subscription status:", error);
          setStatus("error");
        }
      } else {
        setStatus("error");
      }
    };

    verifyPayment();
  }, [searchParams, updateUserSettings, navigate, user]);

  if (status === "loading") {
    return (
      <div className="relative min-h-screen overflow-hidden bg-transparent py-12 checkout-page">
        {/* Animated background elements */}
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
          <div className="absolute -left-64 -top-64 h-[40rem] w-[40rem] rounded-[3rem] bg-purple-600/5 blur-3xl checkout-blur"></div>
          <div className="absolute -bottom-32 -right-32 h-[30rem] w-[30rem] rounded-[3rem] bg-blue-600/5 blur-3xl checkout-blur"></div>
          <div className="animation-delay-2000 absolute right-1/4 top-1/2 h-[20rem] w-[20rem] animate-pulse rounded-[3rem] bg-purple-400/5 blur-3xl checkout-blur"></div>
        </div>

        {/* ... existing loading UI ... */}
      </div>
    );
  }

  if (status === "error") {
    return <Navigate to="/pricing" replace />;
  }

  if (status === "complete") {
    return (
      <div className="relative min-h-screen overflow-hidden py-12 checkout-page">
        {/* Back button for authenticated users */}
        {user && (
          <div className="relative z-20 w-full px-6 py-4">
            <button
              onClick={() => navigate("/chat")}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-transparent border border-purple-500/40 hover:bg-white/10 transition-all duration-200 text-white shadow-md hover:shadow-lg text-sm font-medium"
              aria-label="Back to chat"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Chat
            </button>
          </div>
        )}

        {/* Animated background elements */}
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
          <div className="absolute -left-64 -top-64 h-[40rem] w-[40rem] rounded-[3rem] blur-3xl checkout-blur"></div>
          <div className="absolute -bottom-32 -right-32 h-[30rem] w-[30rem] rounded-[3rem] blur-3xl checkout-blur"></div>
          <div className="animation-delay-2000 absolute right-1/4 top-1/2 h-[20rem] w-[20rem] animate-pulse rounded-[3rem] blur-3xl checkout-blur"></div>
          <div className="absolute left-1/4 top-1/3 h-[25rem] w-[25rem] animate-pulse rounded-[3rem] blur-3xl checkout-blur"></div>

          {/* Floating particles */}
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute h-1 w-1 rounded-full bg-white dark:bg-white light:bg-purple-400"
                style={{
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  opacity: Math.random() * 0.5 + 0.3,
                  animation: `float-up ${Math.random() * 10 + 10}s linear infinite`,
                  animationDelay: `${Math.random() * 10}s`,
                }}
              />
            ))}
          </div>
        </div>

        {/* ... existing complete UI ... */}
      </div>
    );
  }

  return null;
};

const CheckoutPage = () => {
  const [searchParams] = useSearchParams();
  const isReturnFlow = searchParams.has("success");

  // Inject custom animations
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.innerHTML = customAnimations;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  return (
    <div className="min-h-screen">
      {isReturnFlow ? <ReturnPage /> : <CheckoutForm />}
    </div>
  );
};

export default CheckoutPage;
